/* ============================================================
   SEAMLESS INFINITE SLIDER – STABLE (AUTO + FLÈCHES + SWIPE)
   - infinité réelle via clones
   - recoudure INVISIBLE
   - recoudure JAMAIS pendant un drag tactile (sinon casse à droite)
   ============================================================ */
(() => {
  // Singleton guard — évite l'exécution multiple si le script
  // est chargé plusieurs fois (ex: plusieurs sections sur la page)
  if (window.__hbSeamlessSlider) return;
  window.__hbSeamlessSlider = true;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getSlider(component) {
    return component.querySelector('[id^="Slider-"]');
  }

  function isVisible(el) {
    const r = el.getBoundingClientRect();
    return (
      r.width > 0 &&
      r.height > 0 &&
      r.bottom > 0 &&
      r.top < (window.innerHeight || document.documentElement.clientHeight)
    );
  }

  function smoothScroll(slider, left) {
    slider.scrollTo({ left, behavior: 'smooth' });
  }

  function instantJump(slider, left) {
    const prevBehavior = slider.style.scrollBehavior;
    const prevSnapType = slider.style.scrollSnapType;

    slider.style.scrollBehavior = 'auto';
    slider.style.scrollSnapType = 'none';
    slider.scrollLeft = left;
    slider.offsetHeight;

    requestAnimationFrame(() => {
      slider.style.scrollSnapType = prevSnapType;
      slider.style.scrollBehavior = prevBehavior;
    });
  }

  function removeClones(slider) {
    slider.querySelectorAll('[data-clone="1"]').forEach((n) => n.remove());
  }

  function getOriginalSlides(slider) {
    return Array.from(slider.querySelectorAll('[id^="Slide-"]')).filter((el) => !el.dataset.clone);
  }

  function getStep(slider) {
    const slides = Array.from(slider.querySelectorAll('.slider__slide')).filter((el) => el.offsetWidth > 0);
    if (slides.length >= 2) {
      const step = slides[1].offsetLeft - slides[0].offsetLeft;
      if (step > 0) return step;
    }
    return Math.max(1, slider.clientWidth || 1);
  }

  function getSlidesPerView(slider) {
    const step = getStep(slider);
    const approx = Math.round(slider.clientWidth / step);
    return Math.max(1, Math.min(4, approx));
  }

  function buildSeamless(component) {
    const slider = getSlider(component);
    if (!slider) return null;

    removeClones(slider);

    const originals = getOriginalSlides(slider);
    const originalsCount = originals.length;
    if (originalsCount < 2) return null;

    const perView = getSlidesPerView(slider);
    const cloneCount = Math.min(originalsCount, Math.max(1, perView + 1));

    const tail = originals.slice(-cloneCount).map((node) => {
      const c = node.cloneNode(true);
      c.dataset.clone = '1';
      c.removeAttribute('id');
      return c;
    });

    const head = originals.slice(0, cloneCount).map((node) => {
      const c = node.cloneNode(true);
      c.dataset.clone = '1';
      c.removeAttribute('id');
      return c;
    });

    tail.reverse().forEach((c) => slider.insertBefore(c, slider.firstChild));
    head.forEach((c) => slider.appendChild(c));

    const step = getStep(slider);
    const leftBoundary = step * cloneCount;
    const rightBoundary = leftBoundary + step * originalsCount;

    instantJump(slider, leftBoundary);

    return { cloneCount, originalsCount, step, leftBoundary, rightBoundary };
  }

  // Désactive la logique de grisage des boutons de Dawn
  function patchDawnSlider(component) {
    if (component.__dawn_patched) return;
    component.__dawn_patched = true;
    if ('enableSliderLooping' in component) {
      component.enableSliderLooping = true;
    }
  }

  function setup(component) {
    if (prefersReduced) return;
    if (component.__seamless_inited) return;

    const loop = component.dataset.loop === 'true';
    const autostep = component.dataset.autostep === 'true';
    if (!loop) return;

    const slider = getSlider(component);
    if (!slider) return;

    component.__seamless_inited = true;
    component.setAttribute('data-seamless-inited', '1');

    patchDawnSlider(component);

    const interval = parseInt(component.dataset.interval || '3500', 10);

    let meta = null;
    let timer = null;
    let pausedUntil = 0;
    let isDragging = false;

    const pause = (ms = 5000) => (pausedUntil = Date.now() + ms);

    const refresh = () => {
      meta = buildSeamless(component);
    };

    const getBoundaries = () => {
      if (!meta) return null;

      const step = getStep(slider);
      const leftBoundary = step * meta.cloneCount;
      const rightBoundary = leftBoundary + step * meta.originalsCount;
      const originalsSpan = step * meta.originalsCount;

      return { step, leftBoundary, rightBoundary, originalsSpan };
    };

    const normalize = (force = false) => {
      if (!meta) return;
      const b = getBoundaries();
      if (!b) return;

      const { leftBoundary, rightBoundary, originalsSpan } = b;
      const x = slider.scrollLeft;

      if (!force && isDragging) return;

      if (x < leftBoundary) {
        instantJump(slider, x + originalsSpan);
        return;
      }

      if (x >= rightBoundary) {
        instantJump(slider, x - originalsSpan);
      }
    };

    const advance = (dir = 1) => {
      if (!meta) return;
      const b = getBoundaries();
      if (!b) return;

      smoothScroll(slider, slider.scrollLeft + dir * b.step);
    };

    const tick = () => {
      if (Date.now() < pausedUntil) return;
      if (!isVisible(component)) return;
      advance(1);
    };

    const start = () => {
      if (!autostep) return;
      if (timer) return;
      timer = setInterval(tick, interval);
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    // Pause interactions
    slider.addEventListener('scroll', () => pause(2500), { passive: true });
    slider.addEventListener('pointerdown', () => pause(7000), { passive: true });
    slider.addEventListener('touchstart', () => pause(7000), { passive: true });

    // Drag tracking
    const onDragStart = () => { isDragging = true; };
    const onDragEnd = () => {
      isDragging = false;
      requestAnimationFrame(() => normalize(true));
    };

    slider.addEventListener('pointerdown', onDragStart, { passive: true });
    slider.addEventListener('pointerup', onDragEnd, { passive: true });
    slider.addEventListener('pointercancel', onDragEnd, { passive: true });

    slider.addEventListener('touchstart', onDragStart, { passive: true });
    slider.addEventListener('touchend', onDragEnd, { passive: true });
    slider.addEventListener('touchcancel', onDragEnd, { passive: true });

    slider.addEventListener('scroll', () => normalize(false), { passive: true });

    // Flèches — Dawn les déplace parfois hors du slider-component
    const btnScope = component.closest('.slider-4cards-container') || component;
    const nextBtn = btnScope.querySelector('button[name="next"]');
    const prevBtn = btnScope.querySelector('button[name="previous"]');

    function keepButtonsActive() {
      nextBtn && nextBtn.removeAttribute('disabled');
      prevBtn && prevBtn.removeAttribute('disabled');
    }

    const obs = new MutationObserver(keepButtonsActive);
    nextBtn && obs.observe(nextBtn, { attributes: true, attributeFilter: ['disabled'] });
    prevBtn && obs.observe(prevBtn, { attributes: true, attributeFilter: ['disabled'] });
    keepButtonsActive();

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        pause(7000);
        e.preventDefault();
        e.stopImmediatePropagation();
        advance(1);
      }, true);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        pause(7000);
        e.preventDefault();
        e.stopImmediatePropagation();
        advance(-1);
      }, true);
    }

    // Rebuild clones on resize
    let resizeT = null;
    const ro = new ResizeObserver(() => {
      clearTimeout(resizeT);
      resizeT = setTimeout(() => {
        refresh();
        requestAnimationFrame(() => normalize(true));
      }, 120);
    });
    ro.observe(slider);

    // Init — double refresh : immédiat + différé (layout settled)
    refresh();
    setTimeout(() => {
      refresh();
      requestAnimationFrame(() => normalize(true));
    }, 300);

    // Start/Stop selon visibilité (autostep uniquement)
    if (autostep) {
      const io = new IntersectionObserver(
        (entries) => entries.forEach((en) => (en.isIntersecting ? start() : stop())),
        { threshold: 0.25 }
      );
      io.observe(component);
    }
  }

  function initAll() {
    document.querySelectorAll('slider-component[data-loop="true"]').forEach(setup);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);

  // MutationObserver uniquement pour les sliders non encore initialisés
  // (évite le spam causé par les mutations DOM des clones eux-mêmes)
  const mo = new MutationObserver(() => {
    if (document.querySelector('slider-component[data-loop="true"]:not([data-seamless-inited])')) {
      initAll();
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
